import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, LinearProgress, Skeleton, Typography, alpha, useTheme } from '@mui/material';
import { generateQuestionPaperService, QuestionPaperSection } from '../../services/generateQuestionPaperService';
import { MarksDisplayMode, PaperNumberingConfig } from '../../utils/paperNumbering';
import { toUploadUrl } from '../../utils/uploadUrl';
import useDebounce from '../../hooks/useDebounce';
import { useAutoFetch } from '../../hooks/useAutoFetch';

/**
 * The paper exactly as the PDF prints it. The server renders the builder's
 * draft with the same HTML the PDF export feeds to Puppeteer — same Urdu
 * font, maths, numbering and spacing — and this shows it on an A4 sheet.
 * Only the page breaks are left to the download.
 */

interface PaperFormData {
  classId: string;
  subjectId: string;
  title: string;
  description: string;
  instructions: string;
  totalMarks: number;
  duration: number;
  examDate?: string;
  textDirection: 'ltr' | 'rtl';
  marksDisplayMode: MarksDisplayMode;
  numbering: PaperNumberingConfig;
}

interface PaperPreviewProps {
  paperForm: PaperFormData;
  sections: QuestionPaperSection[];
  /** Marks after section rules; printed when the paper has no total yet. */
  currentMarks: number;
}

/** A4 at 96 dpi: 210 mm × 297 mm. */
const SHEET_WIDTH = 794;
const SHEET_HEIGHT = 1123;
/** Wait for typing to settle before re-rendering on the server. */
const REFRESH_DELAY_MS = 400;

const sheetShadow = '0 1px 2px rgba(15,23,42,0.06), 0 12px 32px rgba(15,23,42,0.10)';

const PaperPreview = memo<PaperPreviewProps>(({ paperForm, sections, currentMarks }) => {
  const theme = useTheme();
  const draft = useMemo(
    () => JSON.stringify({ ...paperForm, totalMarks: paperForm.totalMarks || currentMarks, sections }),
    [paperForm, sections, currentMarks],
  );
  const key = useDebounce(draft, REFRESH_DELAY_MS);
  const { data: html, loading, error, retry } = useAutoFetch(
    key,
    () => generateQuestionPaperService.previewPaper(JSON.parse(key)),
  );
  // Files on the page are backend-relative (/uploads, /assets/fonts).
  const srcDoc = useMemo(
    () => (html ? html.replace('<head>', `<head><base href="${toUploadUrl('/')}">`) : null),
    [html],
  );

  // Fit the sheet to narrow screens; never enlarge past real size.
  const stageRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return undefined;
    const observer = new ResizeObserver(([entry]) => setScale(Math.min(1, entry.contentRect.width / SHEET_WIDTH)));
    observer.observe(stage);
    return () => observer.disconnect();
  }, []);

  // The frame grows with its content, so the page scrolls instead of the frame.
  const [height, setHeight] = useState(SHEET_HEIGHT);
  const contentObserver = useRef<ResizeObserver | null>(null);
  useEffect(() => () => contentObserver.current?.disconnect(), []);
  const handleLoad = useCallback((e: React.SyntheticEvent<HTMLIFrameElement>) => {
    const doc = e.currentTarget.contentDocument;
    const view = doc?.defaultView as (Window & typeof globalThis) | null | undefined;
    if (!doc || !view) return;
    const measure = () => setHeight(doc.documentElement.scrollHeight);
    contentObserver.current?.disconnect();
    contentObserver.current = new view.ResizeObserver(measure);
    contentObserver.current.observe(doc.body);
    doc.fonts.ready.then(measure);
  }, []);

  return (
    <Box sx={{
      bgcolor: alpha(theme.palette.text.primary, 0.04),
      borderRadius: 2,
      py: { xs: 2, sm: 4 },
      px: { xs: 1, sm: 3 },
    }}>
      <Box ref={stageRef} sx={{ width: '100%', maxWidth: SHEET_WIDTH, mx: 'auto' }}>
        {error ? (
          <Alert severity="error" action={<Button color="inherit" size="small" onClick={retry}>Retry</Button>}>
            {error}
          </Alert>
        ) : !srcDoc ? (
          <Skeleton
            variant="rectangular"
            sx={{ width: SHEET_WIDTH * scale, height: SHEET_HEIGHT * scale, borderRadius: '2px', mx: 'auto' }}
          />
        ) : (
          <Box sx={{
            position: 'relative',
            width: SHEET_WIDTH * scale,
            height: height * scale,
            mx: 'auto',
            bgcolor: '#ffffff',
            borderRadius: '2px',
            boxShadow: sheetShadow,
            overflow: 'hidden',
          }}>
            {loading && (
              <LinearProgress sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, zIndex: 1 }} />
            )}
            <iframe
              title="Paper preview"
              srcDoc={srcDoc}
              // Same-origin so the frame can be measured; no scripts run in it.
              sandbox="allow-same-origin"
              onLoad={handleLoad}
              style={{
                display: 'block',
                width: SHEET_WIDTH,
                height,
                border: 0,
                transform: `scale(${scale})`,
                transformOrigin: 'top left',
              }}
            />
          </Box>
        )}
        <Typography variant="caption" color="text.secondary" component="p" sx={{ textAlign: 'center', mt: 2 }}>
          Exactly as it prints. Page breaks are added in the downloaded PDF.
        </Typography>
      </Box>
    </Box>
  );
});

PaperPreview.displayName = 'PaperPreview';

export default PaperPreview;
